import { useEffect, useRef, useState, memo } from 'react';
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type Time,
  CrosshairMode,
  LineStyle,
  ColorType,
} from 'lightweight-charts';
import { formatPriceWithSymbol, pricePrecision, isClosedCandle } from '@arc/shared';
import type { UTBotSignal, UTBotConfig, TimeframeKey } from '@arc/shared';
import { useTerminalStore } from '../../stores/terminal.store.js';
import { useUTBot } from '../../hooks/useUTBot.js';
import { useCandleLoader } from '../../hooks/usePriceData.js';
import { selectCandles, selectFeedStatus } from '../../stores/terminal.store.js';
import { TF_DURATION_S } from '../../utils/liveCandles.js';

// ─── Candle Close Timer ───────────────────────────────────────────────────────

function getSecondsRemaining(tf: TimeframeKey): number {
  const dur = TF_DURATION_S[tf];
  const nowS = Date.now() / 1000;
  return dur - (nowS % dur);
}

function formatCountdown(secs: number): string {
  const s = Math.floor(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

function useCandleTimer(tf: TimeframeKey) {
  const [secsLeft, setSecsLeft] = useState(() => getSecondsRemaining(tf));

  useEffect(() => {
    setSecsLeft(getSecondsRemaining(tf));
    const id = setInterval(() => setSecsLeft(getSecondsRemaining(tf)), 1000);
    return () => clearInterval(id);
  }, [tf]);

  return secsLeft;
}

// ─── Theme → Chart Colors ─────────────────────────────────────────────────────

function getChartColors() {
  const style = getComputedStyle(document.documentElement);
  return {
    bg:         style.getPropertyValue('--color-panel').trim()  || '#050e1e',
    border:     style.getPropertyValue('--color-border').trim() || '#1a3a5c',
    text:       style.getPropertyValue('--color-text').trim()   || '#7EC8E3',
    buy:        style.getPropertyValue('--color-buy').trim()    || '#00FF88',
    sell:       style.getPropertyValue('--color-sell').trim()   || '#FF3B3B',
    trail:      style.getPropertyValue('--color-arc').trim()    || '#00BFFF',
  };
}

// ─── Signal Markers ───────────────────────────────────────────────────────────

function buildMarkers(signals: UTBotSignal[], colors: ReturnType<typeof getChartColors>) {
  return signals
    .slice()
    .sort((a, b) => a.timestamp - b.timestamp)
    .map(sig => ({
      time:     Math.floor(sig.timestamp / 1000) as Time,
      position: sig.type === 'BUY' ? 'belowBar' as const : 'aboveBar' as const,
      color:    sig.type === 'BUY' ? colors.buy : colors.sell,
      shape:    sig.type === 'BUY' ? 'arrowUp' as const : 'arrowDown' as const,
      // '?' = forming candle: can still disappear, not traded until it closes
      text:     (sig.type === 'BUY' ? 'B' : 'S') + (isClosedCandle(sig.timestamp, sig.timeframe) ? '' : '?'),
      size:     1.5,
    }));
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ChartPanelProps {
  symbol:    string;
  timeframe: import('@arc/shared').TimeframeKey;
  config:    UTBotConfig;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ChartPanel = memo(function ChartPanel({
  symbol,
  timeframe,
  config,
  className = '',
}: ChartPanelProps) {
  const containerRef    = useRef<HTMLDivElement>(null);
  const chartRef        = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const trailSeriesRef  = useRef<ISeriesApi<'Line'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  // Track prev candle count to distinguish incremental live updates from full reloads
  const prevCandleLenRef = useRef<number>(0);
  // Time of the last candle handed to the chart; update() rejects older times
  const prevLastTimeRef  = useRef<number>(0);
  // Decimal places the price series currently use (see pricePrecision)
  const precisionRef     = useRef<number>(2);

  // Load candles from API
  useCandleLoader(symbol, timeframe);

  // Reset incremental-update tracker whenever symbol or timeframe changes
  // so the next candle batch always triggers a full setData() rather than update()
  useEffect(() => {
    prevCandleLenRef.current = 0;
  }, [symbol, timeframe]);

  const candles  = useTerminalStore(selectCandles(symbol, timeframe));
  const { signals, trailStops } = useUTBot(candles, config, symbol);
  const theme    = useTerminalStore(s => s.theme);
  const feedStatus = useTerminalStore(selectFeedStatus(symbol, timeframe));
  const retryFeeds = useTerminalStore(s => s.retryFeeds);
  const secsLeft = useCandleTimer(timeframe);

  // ── Chart initialization ──────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const colors = getChartColors();

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background:   { type: ColorType.Solid, color: colors.bg },
        textColor:    colors.text,
        fontFamily:   '"Share Tech Mono", monospace',
        fontSize:     11,
      },
      grid: {
        vertLines:   { color: colors.border, style: LineStyle.Dotted },
        horzLines:   { color: colors.border, style: LineStyle.Dotted },
      },
      crosshair: {
        mode:       CrosshairMode.Normal,
        vertLine:   { color: colors.trail, labelBackgroundColor: colors.bg },
        horzLine:   { color: colors.trail, labelBackgroundColor: colors.bg },
      },
      rightPriceScale: {
        borderColor: colors.border,
        textColor:   colors.text,
      },
      timeScale: {
        borderColor:    colors.border,
        timeVisible:    true,
        secondsVisible: false,
      },
      // One formatter for header + axis: thousands separators, same precision.
      localization: { priceFormatter: (p: number) => formatPriceWithSymbol(p) },
      handleScroll:  true,
      handleScale:   true,
    });

    // Candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor:          colors.buy,
      downColor:        colors.sell,
      borderUpColor:    colors.buy,
      borderDownColor:  colors.sell,
      wickUpColor:      colors.buy,
      wickDownColor:    colors.sell,
    });

    // Volume histogram (below candles)
    const volumeSeries = chart.addHistogramSeries({
      priceFormat:    { type: 'volume' },
      priceScaleId:   'volume',
      // Hide the stray last-value tag (was rendering a red '0' on the axis)
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    // UT Bot trail stop line
    const trailSeries = chart.addLineSeries({
      color:       colors.trail,
      lineWidth:   1,
      lineStyle:   LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    chartRef.current        = chart;
    candleSeriesRef.current = candleSeries;
    trailSeriesRef.current  = trailSeries;
    volumeSeriesRef.current = volumeSeries;

    // autoSize: true handles resize automatically — no manual ResizeObserver needed

    return () => {
      chart.remove();
      chartRef.current        = null;
      candleSeriesRef.current = null;
      trailSeriesRef.current  = null;
      volumeSeriesRef.current = null;
    };
  }, []); // Intentionally only on mount

  // ── Re-theme on theme change ──────────────────────────────────────────────
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const c = getChartColors();
    chart.applyOptions({
      layout:     { background: { type: ColorType.Solid, color: c.bg }, textColor: c.text },
      grid:       { vertLines: { color: c.border }, horzLines: { color: c.border } },
      crosshair:  { vertLine: { color: c.trail }, horzLine: { color: c.trail } },
    });
    candleSeriesRef.current?.applyOptions({
      upColor: c.buy, downColor: c.sell,
      borderUpColor: c.buy, borderDownColor: c.sell,
      wickUpColor: c.buy, wickDownColor: c.sell,
    });
    trailSeriesRef.current?.applyOptions({ color: c.trail });
  }, [theme]);

  // ── Push candle data ──────────────────────────────────────────────────────
  useEffect(() => {
    const cs = candleSeriesRef.current;
    const vs = volumeSeriesRef.current;
    if (!cs || !vs) return;

    // No candles for this symbol/timeframe yet (e.g. right after switching
    // symbol): clear the series so the previous symbol's chart doesn't stay
    // on screen behind the loading overlay until the fetch completes.
    if (candles.length === 0) {
      cs.setData([]);
      vs.setData([]);
      trailSeriesRef.current?.setData([]);
      prevCandleLenRef.current = 0;
      return;
    }

    const colors  = getChartColors();
    const last    = candles[candles.length - 1]!;
    const prevLen = prevCandleLenRef.current;

    // The default price format (2 decimals, minMove 0.01) leaves sub-cent
    // tokens with no axis ticks at all; size it to the price instead.
    const precision = pricePrecision(last.close);
    if (precision !== precisionRef.current) {
      precisionRef.current = precision;
      const priceFormat = { type: 'price' as const, precision, minMove: 10 ** -precision };
      cs.applyOptions({ priceFormat });
      trailSeriesRef.current?.applyOptions({ priceFormat });
    }

    // Incremental update: same slot (live price tick) or brand-new candle (+1)
    // — use series.update() to avoid full redraw and scroll-position reset.
    // Full reload (initial load, timeframe/symbol switch): use setData().
    if (prevLen > 0 && (candles.length === prevLen || candles.length === prevLen + 1) && last.time >= prevLastTimeRef.current) {
      cs.update({ time: last.time as Time, open: last.open, high: last.high, low: last.low, close: last.close });
      vs.update({ time: last.time as Time, value: last.volume, color: last.close >= last.open ? colors.buy + '40' : colors.sell + '40' });
    } else {
      cs.setData(candles.map(c => ({
        time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
      })));
      vs.setData(candles.map(c => ({
        time: c.time as Time, value: c.volume, color: c.close >= c.open ? colors.buy + '40' : colors.sell + '40',
      })));
    }
    prevCandleLenRef.current = candles.length;
    prevLastTimeRef.current  = last.time;
  }, [candles]);

  // ── Push trail stop overlay ────────────────────────────────────────────────
  useEffect(() => {
    const ts = trailSeriesRef.current;
    if (!ts) return;
    // Nothing to draw (no candles yet, or too few for the ATR): clear rather
    // than leave the previous symbol's trail line up.
    if (candles.length === 0 || trailStops.length === 0) { ts.setData([]); return; }

    const trailData = candles
      .map((c, i) => ({ time: c.time as Time, value: trailStops[i] ?? 0 }))
      .filter(d => d.value !== 0);

    ts.setData(trailData);
  }, [candles, trailStops]);

  // ── Push signal markers ────────────────────────────────────────────────────
  useEffect(() => {
    const cs = candleSeriesRef.current;
    if (!cs) return;
    const colors = getChartColors();
    cs.setMarkers(buildMarkers(signals, colors));
  }, [signals]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Corner bracket decorations */}
      <div className="corner-tl absolute top-0 left-0" />
      <div className="corner-tr absolute top-0 right-0" />
      <div className="corner-bl absolute bottom-0 left-0" />
      <div className="corner-br absolute bottom-0 right-0" />

      {/* Holo grid overlay */}
      <div className="holo-grid" />

      {/* Chart container */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Candle close countdown + zoom reset */}
      {candles.length > 0 && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5">
          <button
            onMouseDown={e => {
              e.stopPropagation();
              const ts = chartRef.current?.timeScale();
              if (!ts) return;
              const total = candles.length;
              ts.setVisibleLogicalRange({ from: Math.max(0, total - 50), to: total });
            }}
            onKeyDown={e => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              const total = candles.length;
              chartRef.current?.timeScale().setVisibleLogicalRange({ from: Math.max(0, total - 50), to: total });
            }}
            title="Reset zoom"
            aria-label={`Reset ${symbol} chart zoom`}
            className="px-1.5 py-0.5 font-mono text-[11px] tracking-widest uppercase hover:opacity-100 transition-opacity backdrop-blur-sm"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-arc)',
            }}
          >
            ⊡
          </button>
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded cursor-help
                          bg-panel/80 border border-border/50 backdrop-blur-sm"
               title={`Time until the current ${timeframe} candle closes`}
               aria-label={`Current ${timeframe} candle closes in ${formatCountdown(secsLeft)}`}
               tabIndex={0}>
            <span className="text-[11px] font-mono tracking-widest uppercase" style={{ color: 'var(--color-bright)' }}>
              closes
            </span>
            <span
              className="font-mono text-[11px] tabular-nums tracking-wider"
              style={{ color: secsLeft < 30 ? 'var(--color-sell)' : secsLeft < 60 ? '#f59e0b' : 'var(--color-arc)' }}
            >
              {formatCountdown(secsLeft)}
            </span>
          </div>
        </div>
      )}

      {/* No data state */}
      {/* Loading / empty / error states */}
      {candles.length === 0 && (
        // Opaque (chart background colour): the price axis keeps the previous
        // symbol's range until new data arrives, so it must not show through.
        <div className="absolute inset-0 z-20 flex items-center justify-center" role="status" aria-live="polite"
             style={{ background: 'var(--color-panel)' }}>
          {feedStatus === 'loading' ? (
            <div className="text-arc font-mono text-xs tracking-widest animate-pulse">
              FETCHING {symbol} DATA...
            </div>
          ) : (
            <div className="text-center flex flex-col items-center gap-2">
              <div className="font-mono text-xs tracking-widest" style={{ color: feedStatus === 'error' ? 'var(--color-sell)' : 'var(--color-text)' }}>
                {feedStatus === 'error' ? `${symbol} FEED ERROR` : `NO ${symbol} DATA AVAILABLE`}
              </div>
              <button
                onMouseDown={e => e.stopPropagation()}
                onClick={retryFeeds}
                aria-label={`Retry loading ${symbol} data`}
                className="font-mono text-[11px] border border-arc/40 text-arc px-3 py-1 hover:bg-arc/10 transition-colors tracking-widest"
              >
                ↻ RETRY
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
